import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { refreshAdminNotifications } from "@/components/NotificationBell";
import DeleteRequestModal from "@/pages/admin/DeleteRequestModal";
import BatchDeleteModal from "@/pages/admin/BatchDeleteModal";
import SalesBulkBar, { toggleId } from "@/pages/admin/SalesBulkBar";
import {
  ADMIN_OPTIONS, AI_OPTIONS, API_OPTIONS, ASSET_ITEMS, BOARD_BASIC_OPTIONS, BOARD_OPTIONS, BUDGET_OPTIONS,
  CONSULT_STEPS, CONTENT_EXTRAS, CONTENT_READY, CRM_OPTIONS, DESIGN_ASSETS, INTERNAL_WARNINGS,
  MEMBER_BASIC_OPTIONS, MEMBER_OPTIONS, MYPAGE_OPTIONS, PAY_BASIC_OPTIONS, PAY_OPTIONS,
  PURPOSE_OPTIONS, RSV_BASIC_OPTIONS, RSV_OPTIONS, SCALE_OPTIONS,
  SEO_OPTIONS, STATUS_OPTS, TECH_HELP, emptyConsultation,
} from "@/pages/admin/consultationGuide.data";
import DateField, { toDateInputValue } from "@/components/DateField";
import {
  AuditDrawer,
  ConsultationEditBanner,
  EditRequestModal,
  PendingEditQueue,
  RejectEditModal,
  decideEditRequest,
  editBadge,
  submitEditRequest,
} from "@/pages/admin/ConsultationEditLock";
import "./FieldConsultation.css";

function toggle(list, id) {
  const arr = list || [];
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

function Chips({ options, value, onChange, disabled }) {
  return (
    <div className="fc-chips">
      {options.map((opt) => {
        const id = opt.id || opt;
        const label = opt.label || opt;
        const on = (value || []).includes(id);
        return (
          <button key={id} type="button" className={on ? "is-on" : ""} disabled={disabled} onClick={() => { if (!disabled) onChange(toggle(value, id)); }}>
            {label}{opt.hint ? <em> {opt.hint}</em> : null}
          </button>
        );
      })}
    </div>
  );
}

function StatusBar({ value, onChange, disabled }) {
  return (
    <div className="fc-status">
      {STATUS_OPTS.map((s) => (
        <button key={s.id} type="button" className={value === s.id ? "is-on" : ""} disabled={disabled} onClick={() => { if (!disabled) onChange(s.id); }}>{s.label}</button>
      ))}
    </div>
  );
}

function Help({ text }) {
  return <div className="fc-help">{text}</div>;
}

const CONSULT_STATUS_LABEL = {
  DRAFT: "초안",
  CONSULTED: "상담완료",
  NEEDS_CONFIRMATION: "추가확인",
  READY_FOR_ESTIMATE: "견적준비",
  SENT_TO_SALES_ADMIN: "검토요청",
  REQUIREMENTS_RECEIVED: "요구사항 접수",
};
const CONSULT_PREFILL_KEY = "dsite_calc_prefill";

export default function FieldConsultation({ onOpenCalculator }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwner = user?.role === "super_admin";
  const canDuration = isOwner || user?.sales_level === "sales_super_admin";
  const [step, setStep] = useState("basic");
  const [guideMode, setGuideMode] = useState("basic");
  const [shareInfo, setShareInfo] = useState(null);
  const [form, setForm] = useState(emptyConsultation);
  const [list, setList] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [mapped, setMapped] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [listQuery, setListQuery] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editLock, setEditLock] = useState(null);
  const [editRequests, setEditRequests] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [auditItems, setAuditItems] = useState(null);
  const skipUrlConsultRef = useRef(false);

  const answers = form.answers;
  const setAns = (key, patch) => setForm((f) => ({ ...f, answers: { ...f.answers, [key]: { ...f.answers[key], ...patch } } }));

  const unresolved = useMemo(() => {
    const items = [];
    Object.entries(answers).forEach(([k, sec]) => {
      if (sec?.status === "unknown" || sec?.status === "later") items.push(k);
    });
    if (answers.scale?.type === "unknown") items.push("규모 미정");
    if ((answers.board?.values || []).includes("unknown")) items.push("게시판 미정");
    if ((answers.member?.values || []).includes("unknown")) items.push("회원 미정");
    if ((answers.payment?.values || []).includes("unknown")) items.push("결제 미정");
    return items;
  }, [answers]);

  const loadList = async () => {
    try {
      const r = await api.get("/admin/consultations");
      setList(r.data || []);
    } catch {
      toast.error("상담 목록을 불러오지 못했습니다.");
    }
  };
  const loadEditRequests = async () => {
    try {
      const r = await api.get("/admin/consultation-edit-requests");
      setEditRequests(r.data || []);
    } catch {
      setEditRequests([]);
    }
  };
  useEffect(() => { loadList(); loadEditRequests(); }, []);

  const applyDoc = (doc) => {
    setCurrentId(doc.id);
    setForm({
      ...emptyConsultation(),
      ...doc,
      consulted_at: toDateInputValue(doc.consulted_at),
      answers: { ...emptyConsultation().answers, ...(doc.answers || {}) },
    });
    setEditLock(doc.edit_lock || null);
    setMapped(null);
    setStep("basic");
    setEditing(false);
  };

  const save = async (status) => {
    const payload = { ...form, consultation_status: status || form.consultation_status || "CONSULTED" };
    try {
      const r = currentId
        ? await api.patch(`/admin/consultations/${currentId}`, payload)
        : await api.post("/admin/consultations", payload);
      setCurrentId(r.data.id);
      setForm((f) => ({ ...f, consultation_status: r.data.consultation_status || payload.consultation_status }));
      setEditLock(r.data.edit_lock || null);
      setEditing(false);
      toast.success("상담을 저장했습니다.");
      loadList();
      loadEditRequests();
      refreshAdminNotifications();
      return r.data.id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
      return null;
    }
  };

  const openItem = async (doc) => {
    applyDoc(doc);
    try {
      const r = await api.get(`/admin/consultations/${doc.id}`);
      applyDoc(r.data);
    } catch {
      /* list payload is enough to view */
    }
  };

  const resetLocalSession = () => {
    skipUrlConsultRef.current = true;
    setCurrentId(null);
    setMapped(null);
    setStep("basic");
    setForm(emptyConsultation());
    setEditing(false);
    setEditLock(null);
    setShareInfo(null);
    try { sessionStorage.removeItem(CONSULT_PREFILL_KEY); } catch { /* ignore */ }
    setSearchParams({ tab: "field-consultation" });
  };

  const startNewConsult = () => {
    const dirty = Boolean(currentId || form.company_name || form.contact_name);
    if (dirty) setResetOpen(true);
    else resetLocalSession();
  };

  const visibleList = useMemo(() => {
    const needle = listQuery.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((c) => [c.company_name, c.contact_name, c.consultation_status].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [list, listQuery]);
  const visibleIds = visibleList.map((c) => c.id);

  useEffect(() => {
    const consultId = searchParams.get("consult");
    if (skipUrlConsultRef.current) {
      if (!consultId) skipUrlConsultRef.current = false;
      return;
    }
    if (!consultId || !list.length) return;
    const doc = list.find((c) => c.id === consultId);
    if (doc && currentId !== consultId) openItem(doc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, list, currentId]);

  useEffect(() => {
    const consultId = searchParams.get("consult");
    if (!consultId) return;
    const el = document.querySelector(`[data-testid="consult-card-${consultId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [searchParams, list]);

  const runHardDelete = async () => {
    if (!hardDeleteTarget || hardDeleting) return;
    setHardDeleting(true);
    try {
      await api.delete(`/admin/consultations/${hardDeleteTarget.id}/hard`);
      toast.success("상담이 삭제되었습니다.");
      const deletedId = hardDeleteTarget.id;
      setHardDeleteTarget(null);
      setList((prev) => prev.filter((c) => c.id !== deletedId));
      if (currentId === deletedId) resetLocalSession();
      loadList();
      refreshAdminNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "삭제 실패");
    } finally {
      setHardDeleting(false);
    }
  };

  const isCompleted = Boolean(currentId && form.consultation_status && form.consultation_status !== "DRAFT");
  const viewLocked = isCompleted && !editing;
  const pendingEdits = isOwner ? editRequests.filter((r) => r.status === "PENDING") : [];
  const requestByConsult = Object.fromEntries(editRequests.map((r) => [r.consultation_id, r]));

  const toCalculator = async (preferredFlow) => {
    const id = currentId || await save("READY_FOR_ESTIMATE");
    if (!id) return;
    try {
      if (!viewLocked) await save("READY_FOR_ESTIMATE");
      else await api.patch(`/admin/consultations/${id}`, { ...form, consultation_status: "READY_FOR_ESTIMATE" });
      const r = await api.post(`/admin/consultations/${id}/to-calculator`);
      const mapped = r.data || {};
      sessionStorage.setItem(CONSULT_PREFILL_KEY, JSON.stringify(mapped));
      if (mapped.canonical_answers || mapped.v2_prefill) {
        const wizard = preferredFlow === "custom" || mapped.wizard_flow === "custom" ? "custom" : "recommend";
        sessionStorage.setItem("dsite_v2_prefill", JSON.stringify({
          wizard_flow: wizard,
          flow_type: mapped.flow_type || "",
          canonical_answers: mapped.canonical_answers || {},
          wizard_answers: mapped.wizard_answers || mapped.canonical_answers || {},
          custom_requests: mapped.custom_requests || {},
        }));
        window.dispatchEvent(new Event("dsite-v2-prefill"));
      }
      toast.success("계산기로 가져갈 항목을 준비했습니다. 고객 제출만으로 견적이 생성되지는 않습니다.");
      onOpenCalculator?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "연결 실패");
    }
  };

  const previewMap = async () => {
    const id = currentId || await save("DRAFT");
    if (!id) return;
    const r = await api.post(`/admin/consultations/${id}/to-calculator`);
    setMapped(r.data);
  };

  const paySelected = (answers.payment?.values || []).some((v) => ["pg", "card", "pg_standard"].includes(v));
  const memberSelected = (answers.member?.values || []).some((v) => ["signup", "login", "signup_login"].includes(v));
  const rsvSelected = (answers.reservation?.values || []).some((v) => v && v !== "none" && v !== "unknown");
  const shopOrBooking = ["shop", "booking", "custom", "platform"].includes(answers.scale?.type);
  const visibleSteps = CONSULT_STEPS.filter((s) => guideMode === "detail" || s.tier !== "detail");

  const createShareLink = async () => {
    const id = currentId || await save("DRAFT");
    if (!id) return;
    try {
      const r = await api.post(`/admin/consultations/${id}/share-link`, { days: 14 });
      setShareInfo(r.data);
      const url = r.data.url;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      toast.success("고객 상담가이드 링크를 만들었습니다. 클립보드에 복사했습니다.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "링크 생성 실패");
    }
  };

  const loadShareLink = async (id) => {
    if (!id) { setShareInfo(null); return; }
    try {
      const r = await api.get(`/admin/consultations/${id}/share-link`);
      setShareInfo(r.data?.token ? r.data : null);
    } catch { setShareInfo(null); }
  };

  const revokeShareLink = async () => {
    if (!currentId) return;
    try {
      const r = await api.patch(`/admin/consultations/${currentId}/share-link`, { revoked: true });
      setShareInfo(r.data);
      toast.success("링크 사용을 중지했습니다.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "중지 실패");
    }
  };

  const importCustomerIntake = async () => {
    if (!currentId) return;
    try {
      const r = await api.post(`/admin/consultations/${currentId}/import-customer-intake`);
      const next = r.data.consultation || {};
      applyDoc(next);
      toast.success("고객 작성 내용을 상담 폼에 반영했습니다. 이 단계에서는 가격을 계산하지 않습니다.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "불러오기 실패");
    }
  };

  useEffect(() => { loadShareLink(currentId); }, [currentId]);

  return (
    <div className="fc" data-testid="field-consultation">
      <div className="fc-head">
        <div>
          <h3 className="font-display text-lg">현장 상담 가이드</h3>
          <p className="text-xs text-white/40 mt-1">고객과 대화하며 요구사항을 빠짐없이 확인합니다. 미정 항목은 계산기에 자동 반영되지 않습니다.</p>
        </div>
        <div className="fc-head-actions">
          <button type="button" className="fc-btn" onClick={startNewConsult} data-testid="consult-new">+ 새 상담</button>
          <button type="button" className="fc-btn ghost" onClick={createShareLink} data-testid="consult-share-link">고객 상담가이드 링크</button>
          <button type="button" className="fc-btn ghost fc-btn-reset" onClick={() => setResetOpen(true)} data-testid="consult-full-reset">전체 리셋</button>
          {!viewLocked && (
            <button type="button" className="fc-btn" onClick={() => save(isCompleted ? form.consultation_status : "CONSULTED")} data-testid="consult-save">
              {editing ? "저장" : "상담 저장"}
            </button>
          )}
          {editing && (
            <button type="button" className="fc-btn ghost" onClick={() => { setEditing(false); if (currentId) openItem({ id: currentId }); }}>취소</button>
          )}
          {(user?.sales_level === "team_leader" || user?.sales_level === "member") && (
            <button type="button" className="fc-btn" onClick={() => save("SENT_TO_SALES_ADMIN")} data-testid="consult-send">영업총괄에게 전달</button>
          )}
          {user?.sales_level === "sales_super_admin" && (form.consultation_status === "SENT_TO_SALES_ADMIN" || list.find((c) => c.id === currentId)?.consultation_status === "SENT_TO_SALES_ADMIN") && (
            <button type="button" className="fc-btn" onClick={() => save("READY_FOR_ESTIMATE")} data-testid="consult-review-done">검토 완료</button>
          )}
        </div>
      </div>

      <ol className="sales-flow" data-testid="sales-flow">
        <li className="is-on">1 상담 만들기</li>
        <li>2 고객 요구사항 받기</li>
        <li>3 견적 계산</li>
        <li>4 견적 저장</li>
        <li>5 계약 전환</li>
      </ol>

      {viewLocked && (
        <ConsultationEditBanner
          lock={editLock || { locked: true, can_edit: isOwner, can_request: !isOwner, request: requestByConsult[currentId] }}
          isOwner={isOwner}
          onRequest={() => setRequestOpen(true)}
          onStartEdit={() => setEditing(true)}
          onOpenAudit={async () => {
            try {
              const r = await api.get(`/admin/consultations/${currentId}/edit-audits`);
              setAuditItems(r.data.items || []);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "이력을 불러오지 못했습니다.");
            }
          }}
        />
      )}

      <div className="fc-mode">
        <button type="button" className={guideMode === "basic" ? "is-on" : ""} onClick={() => { setGuideMode("basic"); if (step === "detail") setStep("needs"); }}>기본 상담</button>
        <button type="button" className={guideMode === "detail" ? "is-on" : ""} onClick={() => setGuideMode("detail")}>상세 상담</button>
        <span>기본 화면은 꼭 물어야 하는 질문만 보여 줍니다.</span>
      </div>
      {shareInfo?.url && (
        <div className="fc-share" data-testid="consult-share-box">
          <div>
            고객 상담가이드 링크
            {shareInfo.revoked ? " · 비활성" : shareInfo.active ? " · 활성" : " · 만료/중지"}
            {shareInfo.submitted ? " · 제출됨" : " · 미제출"}
          </div>
          <div className="fc-share-meta">
            생성 {String(shareInfo.created_at || "").slice(0, 16) || "-"}
            {" · "}만료 {String(shareInfo.expires_at || "").slice(0, 16) || "-"}
            {shareInfo.submitted_at ? ` · 제출 ${String(shareInfo.submitted_at).slice(0, 16)}` : ""}
          </div>
          <input readOnly value={shareInfo.url} onFocus={(e) => e.target.select()} />
          <button type="button" className="fc-btn ghost" onClick={() => navigator.clipboard.writeText(shareInfo.url)}>링크 복사</button>
          {!shareInfo.revoked && shareInfo.active && <button type="button" className="fc-btn ghost" onClick={revokeShareLink}>링크 비활성화</button>}
          <button type="button" className="fc-btn ghost" onClick={createShareLink}>새 링크 발급</button>
        </div>
      )}
      {(form.customer_intake_view?.submitted_at || form.customer_intake?.submitted_at) && (
        <div className="fc-intake" data-testid="consult-customer-intake">
          <h4>고객 요구사항 접수 완료</h4>
          <p className="fc-next-hint">다음 단계: 견적 계산기로 불러오기</p>
          <div>상담 방식 {form.customer_intake_view?.flow_label || form.customer_intake_view?.flow_type || "기존 상담가이드"}</div>
          <div>추천 구축 유형 {form.customer_intake_view?.recommended_product_label || form.customer_intake_view?.site_type || "-"}</div>
          <div>기본정보 {form.customer_intake_view?.company_name || form.company_name || "-"} / {form.customer_intake_view?.contact_name || "-"} / {form.customer_intake_view?.phone || "-"} / {form.customer_intake_view?.email || "-"}</div>
          <div>고객 주요 요청 {(form.customer_intake_view?.confirmed || []).join(", ") || (form.customer_intake_view?.purposes || []).join(", ") || "-"}</div>
          <div>추가 확인 {(form.customer_intake_view?.additional_checks || form.customer_intake_view?.recommend_items || []).join(" / ") || "-"}</div>
          <div>선택 기능 {(form.customer_intake_view?.features || []).filter((f) => !f.consult_recommend).map((f) => f.customer_label || f.key).join(", ") || "-"}</div>
          <div>추가 요청 {form.customer_intake_view?.extra_request || form.customer_intake_view?.notes || "-"}</div>
          {form.customer_intake_view?.custom_notes ? <div>기타 요청 원문 {form.customer_intake_view.custom_notes}</div> : null}
          <div>일정 {form.customer_intake_view?.hoped_open || "-"}{form.customer_intake_view?.deadline ? ` / 마감 ${form.customer_intake_view.deadline}` : ""}</div>
          {Object.keys(form.customer_intake_view?.extra_requests || {}).length ? (
            <div>영역별 추가 입력 {Object.entries(form.customer_intake_view.extra_requests).map(([k, v]) => `${k}: ${v}`).join(" / ")}</div>
          ) : null}
          <div className="fc-actions">
            <button type="button" className="fc-btn" onClick={() => toCalculator()} data-testid="consult-intake-to-calc">견적 계산기로 불러오기</button>
            <button type="button" className="fc-btn ghost" onClick={importCustomerIntake} data-testid="consult-import-intake">고객 요구사항 불러오기</button>
            <button type="button" className="fc-btn ghost" onClick={() => toCalculator("recommend")} data-testid="consult-load-recommend">추천 견적으로 불러오기</button>
            <button type="button" className="fc-btn ghost" onClick={() => toCalculator("custom")} data-testid="consult-load-custom">맞춤형 견적으로 불러오기</button>
          </div>
        </div>
      )}

      <div className="fc-progress">
        {visibleSteps.map((s, i) => (
          <button key={s.id} type="button" className={step === s.id ? "is-on" : ""} onClick={() => setStep(s.id)}>
            {String(i + 1).padStart(2, "0")}
          </button>
        ))}
        <span className="fc-unresolved">추가 확인 필요 {unresolved.length}건</span>
      </div>

      <div className="fc-layout">
        <section className={`fc-main${viewLocked ? " is-readonly" : ""}`}>
          {step === "basic" && (
            <div className="fc-card">
              <h4>기본정보</h4>
              <div className="fc-grid">
                <input placeholder="고객명 / 업체명" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                <input placeholder="담당자명" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                <input placeholder="연락처" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input placeholder="이메일" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input placeholder="업종" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
                <DateField label="상담일" value={form.consulted_at} onChange={(v) => setForm({ ...form, consulted_at: v })} disabled={viewLocked} testId="consult-date" />
                <input placeholder="영업 담당자" value={answers.basic.salesperson} onChange={(e) => setAns("basic", { salesperson: e.target.value })} />
                <input placeholder="기존 홈페이지 URL" value={answers.basic.existing_url} onChange={(e) => setAns("basic", { existing_url: e.target.value })} />
                <input placeholder="참고 홈페이지 URL" value={answers.basic.ref_url} onChange={(e) => setAns("basic", { ref_url: e.target.value })} />
                <input placeholder="희망 오픈시점 (확정 납기 아님)" value={form.hoped_open} onChange={(e) => setForm({ ...form, hoped_open: e.target.value })} />
                <select value={form.budget_range} onChange={(e) => setForm({ ...form, budget_range: e.target.value })}>
                  {BUDGET_OPTIONS.map((b) => <option key={b}>{b}</option>)}
                </select>
              </div>
              <textarea rows={3} placeholder="상담 메모" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
              <Help>희망 오픈일은 확정 납기로 취급하지 않습니다. 예산에 맞춰 계산기 가격을 자동으로 낮추지 않습니다.</Help>
            </div>
          )}

          {step === "core" && (
            <div className="fc-card">
              <h4>홈페이지를 제작하려는 가장 큰 목적이 무엇인가요?</h4>
              <Chips disabled={viewLocked} options={PURPOSE_OPTIONS} value={answers.purpose.values} onChange={(v) => setAns("purpose", { values: v })} />
              <div className="fc-grid">
                <select value={answers.purpose.is_new} onChange={(e) => setAns("purpose", { is_new: e.target.value })}>
                  <option value="">신규 / 리뉴얼</option>
                  <option value="new">신규 제작</option>
                  <option value="renew">기존 홈페이지 리뉴얼</option>
                </select>
                <input placeholder="기존 홈페이지가 있다면 가장 불편한 점은?" value={answers.purpose.pain} onChange={(e) => setAns("purpose", { pain: e.target.value })} />
                <input placeholder="방문자가 최종적으로 어떤 행동을 하길 원하는가?" value={answers.purpose.desired_action} onChange={(e) => setAns("purpose", { desired_action: e.target.value })} />
              </div>
              <StatusBar disabled={viewLocked} value={answers.purpose.status} onChange={(v) => setAns("purpose", { status: v })} />
            </div>
          )}

          {step === "core" && (
            <div className="fc-card">
              <h4>사이트 규모</h4>
              <Chips disabled={viewLocked} options={SCALE_OPTIONS} value={answers.scale.type ? [answers.scale.type] : []} onChange={(v) => setAns("scale", { type: v[v.length - 1] || "" })} />
              <div className="fc-grid">
                <input placeholder="메인 페이지 수 (미정 가능)" value={answers.scale.main_pages} onChange={(e) => setAns("scale", { main_pages: e.target.value })} />
                <input placeholder="서브 페이지 수 (미정 가능)" value={answers.scale.sub_pages} onChange={(e) => setAns("scale", { sub_pages: e.target.value })} />
              </div>
              <StatusBar disabled={viewLocked} value={answers.scale.status} onChange={(v) => setAns("scale", { status: v })} />
              <Help>직접 구성형 견적이 가능합니다. 큰 패키지를 강제하지 않습니다.</Help>
            </div>
          )}

          {step === "detail" && (
            <div className="fc-card">
              <h4>기존 디자인 자료가 있으신가요?</h4>
              <Chips disabled={viewLocked} options={DESIGN_ASSETS} value={answers.design.assets} onChange={(v) => setAns("design", { assets: v })} />
              <div className="fc-grid">
                <select value={answers.design.level} onChange={(e) => setAns("design", { level: e.target.value })}>
                  <option value="">디자인 수준</option>
                  <option value="basic">기본형</option>
                  <option value="custom">맞춤형</option>
                  <option value="uiux">고급 UI/UX</option>
                  <option value="interaction">인터랙션 중심</option>
                  <option value="unknown">미정</option>
                </select>
                <select value={answers.design.mobile} onChange={(e) => setAns("design", { mobile: e.target.value })}>
                  <option value="">모바일</option>
                  <option value="responsive">반응형이면 충분</option>
                  <option value="separate">모바일 별도 디자인 필요</option>
                  <option value="unknown">미정</option>
                </select>
              </div>
              <StatusBar disabled={viewLocked} value={answers.design.status} onChange={(v) => setAns("design", { status: v })} />
            </div>
          )}

          {step === "detail" && (
            <div className="fc-card">
              <h4>홈페이지에 들어갈 문구, 사진, 서비스 설명 등의 자료는 어느 정도 준비되어 있으신가요?</h4>
              <Help>콘텐츠 기획/제작 비용은 페이지 디자인비 및 개발비와 별도입니다. 고객이 모든 자료를 제공하면 0원입니다.</Help>
              <Chips disabled={viewLocked} options={CONTENT_READY} value={answers.content.readiness ? [answers.content.readiness] : []} onChange={(v) => setAns("content", { readiness: v[v.length - 1] || "" })} />
              <div className="text-xs text-white/45 mt-3 mb-1">추가 옵션 (별도 선택)</div>
              <Chips disabled={viewLocked} options={CONTENT_EXTRAS} value={answers.content.extras} onChange={(v) => setAns("content", { extras: v })} />
              <StatusBar disabled={viewLocked} value={answers.content.status} onChange={(v) => setAns("content", { status: v })} />
            </div>
          )}

          {step === "needs" && (
            <div className="fc-card">
              <h4>홈페이지에서 직접 게시물을 올리거나 관리해야 하나요?</h4>
              <Chips disabled={viewLocked} options={guideMode === "detail" ? BOARD_OPTIONS : BOARD_BASIC_OPTIONS} value={answers.board.values} onChange={(v) => setAns("board", { values: v })} />
              <StatusBar disabled={viewLocked} value={answers.board.status} onChange={(v) => setAns("board", { status: v })} />
            </div>
          )}

          {step === "needs" && (
            <div className="fc-card">
              <h4>고객이 회원가입하거나 로그인해야 하나요?</h4>
              <Chips disabled={viewLocked} options={guideMode === "detail" ? MEMBER_OPTIONS : MEMBER_BASIC_OPTIONS} value={answers.member.values} onChange={(v) => setAns("member", { values: v })} />
              {memberSelected && (
                <>
                  <Help>마이페이지도 필요한가요?</Help>
                  <h4>마이페이지</h4>
                  <Chips disabled={viewLocked} options={MYPAGE_OPTIONS} value={answers.mypage.values} onChange={(v) => setAns("mypage", { values: v })} />
                </>
              )}
              {(shopOrBooking || paySelected || guideMode === "detail") && (
                <>
                  <h4>홈페이지 안에서 실제 결제가 이루어져야 하나요?</h4>
                  <Chips disabled={viewLocked} options={guideMode === "detail" ? PAY_OPTIONS : PAY_BASIC_OPTIONS} value={answers.payment.values} onChange={(v) => setAns("payment", { values: v })} />
                  {paySelected && (
                    <Help>PG 연동만으로 전체 결제 시스템이 완성되는 것이 아닙니다. 주문 DB, 고객 주문내역, 관리자 주문관리, 취소/부분취소/환불, 회원/비회원 결제를 추가로 확인해 주세요. 자동으로 유료항목을 넣지 않습니다.</Help>
                  )}
                </>
              )}
              {(shopOrBooking || rsvSelected || answers.scale?.type === "booking" || guideMode === "detail") && (
                <>
                  <h4>온라인 예약을 받아야 하나요?</h4>
                  <Chips disabled={viewLocked} options={guideMode === "detail" ? RSV_OPTIONS : RSV_BASIC_OPTIONS} value={answers.reservation.values} onChange={(v) => setAns("reservation", { values: v })} />
                </>
              )}
              <StatusBar disabled={viewLocked} value={answers.member.status} onChange={(v) => setAns("member", { status: v })} />
            </div>
          )}

          {step === "detail" && (
            <div className="fc-card">
              <h4>고객사에서 직접 관리해야 하는 내용은 무엇인가요?</h4>
              <Help>단순히 “관리자 페이지 필요합니다”라고 하면, 무엇을 관리해야 하는지 세분화해서 확인하세요.</Help>
              <Chips disabled={viewLocked} options={ADMIN_OPTIONS} value={answers.admin.values} onChange={(v) => setAns("admin", { values: v })} />
              <h4>CRM</h4>
              <Chips disabled={viewLocked} options={CRM_OPTIONS} value={answers.crm.values} onChange={(v) => setAns("crm", { values: v })} />
              <h4>현재 사용 중인 외부 서비스와 홈페이지를 연결해야 하나요?</h4>
              <Chips disabled={viewLocked} options={API_OPTIONS} value={answers.integration.values} onChange={(v) => setAns("integration", { values: v })} />
              <div className="fc-grid">
                <input placeholder="API 문서 보유?" value={answers.integration.api_docs} onChange={(e) => setAns("integration", { api_docs: e.target.value })} />
                <input placeholder="기존 서비스 계정 보유?" value={answers.integration.account} onChange={(e) => setAns("integration", { account: e.target.value })} />
                <input placeholder="외부 서비스 비용 발생?" value={answers.integration.extra_cost} onChange={(e) => setAns("integration", { extra_cost: e.target.value })} />
                <input placeholder="기존 개발업체 협조 필요?" value={answers.integration.vendor} onChange={(e) => setAns("integration", { vendor: e.target.value })} />
              </div>
              <h4>AI / 자동화</h4>
              <Chips disabled={viewLocked} options={AI_OPTIONS} value={answers.ai.values} onChange={(v) => setAns("ai", { values: v })} />
              <h4>검색엔진 또는 AI 검색 노출까지 필요하신가요?</h4>
              <Help>검색 순위를 보장한다는 표현은 사용하지 않습니다.</Help>
              <Chips disabled={viewLocked} options={SEO_OPTIONS} value={answers.seo.values} onChange={(v) => setAns("seo", { values: v })} />
            </div>
          )}

          {step === "detail" && (
            <div className="fc-card">
              <h4>서버 / 운영</h4>
              <div className="fc-grid">
                <select value={answers.infra.domain} onChange={(e) => setAns("infra", { domain: e.target.value })}>
                  <option value="">기존 도메인</option><option>있음</option><option>없음</option>
                </select>
                <input placeholder="도메인 소유자" value={answers.infra.domain_owner} onChange={(e) => setAns("infra", { domain_owner: e.target.value })} />
                <select value={answers.infra.hosting} onChange={(e) => setAns("infra", { hosting: e.target.value })}>
                  <option value="">기존 Hosting</option><option>있음</option><option>없음</option>
                </select>
                <select value={answers.infra.server} onChange={(e) => setAns("infra", { server: e.target.value })}>
                  <option value="">기존 Server</option><option>있음</option><option>없음</option>
                </select>
                <select value={answers.infra.ops_payer} onChange={(e) => setAns("infra", { ops_payer: e.target.value })}>
                  <option>미정</option><option>고객 직접결제</option><option>DSITESOLUTION 관리</option>
                </select>
                <select value={answers.maintenance.plan} onChange={(e) => setAns("maintenance", { plan: e.target.value })}>
                  <option value="">유지보수</option>
                  <option value="self">고객 직접 관리</option>
                  <option value="basic">BASIC</option>
                  <option value="business">BUSINESS</option>
                  <option value="pro">PRO</option>
                  <option value="custom">CUSTOM</option>
                  <option value="unknown">미정</option>
                </select>
              </div>
              <Chips
                disabled={viewLocked}
                options={[{ id: "backend", label: "Backend 필요" }, { id: "database", label: "Database 필요" }, { id: "migrate", label: "기존 DB 이전" }, { id: "ssl", label: "SSL" }, { id: "backup", label: "Backup" }, { id: "monitor", label: "Monitoring" }]}
                value={["backend", "database", "migrate", "ssl", "backup", "monitor"].filter((k) => answers.infra[k])}
                onChange={(v) => setAns("infra", Object.fromEntries(["backend", "database", "migrate", "ssl", "backup", "monitor"].map((k) => [k, v.includes(k)])))}
              />
              <Help>Domain / Hosting / Backend / Database / PG / 문자 / 알림톡 / Email / 외부 API / AI API / 지도 API 등은 외부 정책·사용량에 따라 추가 비용이 날 수 있습니다. “전부 무료” 또는 “서버비 고정”으로 확정해서 말하지 마세요.</Help>
              <StatusBar disabled={viewLocked} value={answers.infra.status} onChange={(v) => setAns("infra", { status: v })} />
            </div>
          )}

          {step === "detail" && (
            <div className="fc-card">
              <h4>예산 / 일정 / 자료</h4>
              <div className="fc-grid">
                <input placeholder="희망 오픈일" value={form.hoped_open} onChange={(e) => setForm({ ...form, hoped_open: e.target.value })} />
                {canDuration && <input placeholder="참고 예상기간 (선택)" value={form.duration_note} onChange={(e) => setForm({ ...form, duration_note: e.target.value })} />}
              </div>
              <Help>예상 제작기간은 기본적으로 “협의 후 확정”입니다. 자동 계산된 기간을 확정 납기로 보여주지 않습니다.</Help>
              <div className="text-xs text-white/45 mb-2">고객 자료 전달</div>
              {ASSET_ITEMS.map((name) => (
                <label key={name} className="fc-asset">
                  <span>{name}</span>
                  <select
                    value={answers.assets.items[name] || ""}
                    onChange={(e) => setAns("assets", { items: { ...answers.assets.items, [name]: e.target.value } })}
                  >
                    <option value="">선택</option>
                    <option>보유</option>
                    <option>추후 전달</option>
                    <option>없음</option>
                    <option>해당 없음</option>
                  </select>
                </label>
              ))}
              <h4 className="mt-4">의사결정 구조</h4>
              <div className="fc-grid">
                <input placeholder="최종 의사결정자" value={answers.decision.decision_maker} onChange={(e) => setAns("decision", { decision_maker: e.target.value })} />
                <input placeholder="현재 상담자가 결정권자인가?" value={answers.decision.is_decider} onChange={(e) => setAns("decision", { is_decider: e.target.value })} />
                <input placeholder="추가 승인자" value={answers.decision.extra_approver} onChange={(e) => setAns("decision", { extra_approver: e.target.value })} />
                <input placeholder="디자인 검수 담당자" value={answers.decision.design_review} onChange={(e) => setAns("decision", { design_review: e.target.value })} />
                <input placeholder="기능 검수 담당자" value={answers.decision.feature_review} onChange={(e) => setAns("decision", { feature_review: e.target.value })} />
                <input placeholder="최종 결재 담당자" value={answers.decision.final_sign} onChange={(e) => setAns("decision", { final_sign: e.target.value })} />
                <input placeholder="견적서 수신자" value={answers.decision.quote_to} onChange={(e) => setAns("decision", { quote_to: e.target.value })} />
                <input placeholder="계약 담당자" value={answers.decision.contract_to} onChange={(e) => setAns("decision", { contract_to: e.target.value })} />
                <input placeholder="세금계산서 담당자" value={answers.decision.tax_to} onChange={(e) => setAns("decision", { tax_to: e.target.value })} />
              </div>
            </div>
          )}

          {step === "confirm" && (
            <div className="fc-card" data-testid="consult-summary">
              <h4>상담 Summary</h4>
              <div className="fc-summary">
                <div>고객 {form.company_name || "-"} / {form.contact_name || "-"}</div>
                <div>목적 {(answers.purpose.values || []).join(", ") || "-"}</div>
                <div>형태 {SCALE_OPTIONS.find((s) => s.id === answers.scale.type)?.label || "-"}</div>
                <div>페이지 메인 {answers.scale.main_pages || "-"} / 서브 {answers.scale.sub_pages || "-"}</div>
                <div>콘텐츠 {CONTENT_READY.find((c) => c.id === answers.content.readiness)?.label || "-"}</div>
                <div>게시판 {(answers.board.values || []).join(", ") || "-"}</div>
                <div>회원 {(answers.member.values || []).join(", ") || "-"}</div>
                <div>PG {(answers.payment.values || []).join(", ") || "-"}</div>
                <div>예약 {(answers.reservation.values || []).join(", ") || "-"}</div>
                <div>관리자 {(answers.admin.values || []).join(", ") || "-"}</div>
                <div>예산 {form.budget_range}</div>
                <div>미정사항 {unresolved.length}건</div>
                <div>특이사항 {form.memo || "-"}</div>
                <div>제작기간 협의 후 확정{form.duration_note ? ` / 참고 예상기간 ${form.duration_note}` : ""}</div>
              </div>
              <div className="fc-actions">
                <button type="button" className="fc-btn ghost" onClick={previewMap}>매핑 미리보기</button>
                <button type="button" className="fc-btn" onClick={toCalculator} data-testid="consult-to-calc">견적 계산기로 불러오기</button>
              </div>
              {mapped && (
                <div className="fc-map">
                  <div>선택 {mapped.lines?.length || 0}개 · 미정 {mapped.unresolved_count || 0}건</div>
                  {(mapped.custom_needed || []).length > 0 && <div>별도 견적 필요: {mapped.custom_needed.join(", ")}</div>}
                  {(mapped.suggestions || []).length > 0 && <div>추천(자동선택 아님): {mapped.suggestions.join(", ")}</div>}
                </div>
              )}
            </div>
          )}

          <aside className="fc-internal">
            <div className="fc-internal-title">영업직 내부 안내</div>
            <div>개발 검토 전 확정해서 말하면 안 되는 것:</div>
            <ul>{INTERNAL_WARNINGS.map((w) => <li key={w}>{w}</li>)}</ul>
            <div className="mt-2">용어</div>
            <ul>{TECH_HELP.map((t) => <li key={t.term}><b>{t.term}</b> {t.text}</li>)}</ul>
          </aside>
        </section>

        <aside className="fc-list">
          {isOwner && (
            <PendingEditQueue
              items={pendingEdits}
              onOpen={(row) => {
                const doc = list.find((c) => c.id === row.consultation_id);
                if (doc) openItem(doc);
                else setSearchParams({ tab: "field-consultation", consult: row.consultation_id });
              }}
              onApprove={async (row) => {
                try {
                  await decideEditRequest(row.id, true);
                  toast.success("수정 권한을 승인했습니다.");
                  loadEditRequests();
                  if (currentId) openItem(list.find((c) => c.id === currentId) || { id: currentId });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "승인 실패");
                }
              }}
              onReject={(row) => setRejectTarget(row)}
            />
          )}
          <div className="fc-list-title">상담 기록</div>
          <input
            className="fc-list-search"
            value={listQuery}
            onChange={(e) => setListQuery(e.target.value)}
            placeholder="고객명 / 담당자 검색"
            data-testid="consult-list-search"
          />
          <SalesBulkBar
            visibleIds={visibleIds}
            selectedIds={selectedIds}
            onToggleAll={(on) => setSelectedIds(on ? visibleIds : [])}
            onDelete={() => setBatchOpen(true)}
            deleteLabel={isOwner ? "선택항목 삭제" : "선택항목 삭제 요청"}
            testId="consult-bulk-bar"
          />
          {visibleList.length === 0 && (
            <div className="fc-empty" data-testid="consult-empty">
              <p>아직 상담이 없습니다.</p>
              <button type="button" className="fc-btn" onClick={startNewConsult} data-testid="consult-empty-new">+ 새 상담 시작</button>
            </div>
          )}
          {visibleList.map((c) => (
            <div
              key={c.id}
              data-testid={`consult-card-${c.id}`}
              className={`fc-list-item ${currentId === c.id ? "is-on" : ""} ${searchParams.get("consult") === c.id ? "is-focus" : ""}`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(c.id)}
                onChange={(e) => setSelectedIds((prev) => toggleId(prev, c.id, e.target.checked))}
                data-testid={`consult-check-${c.id}`}
              />
              <button type="button" onClick={() => openItem(c)}>
                <div>{c.company_name || c.contact_name || "제목 없음"}</div>
                <div className="meta">
                  {CONSULT_STATUS_LABEL[c.consultation_status] || c.consultation_status}
                  {c.consultation_status && c.consultation_status !== "DRAFT" ? " · 완료" : ""}
                  {editBadge({ locked: c.consultation_status && c.consultation_status !== "DRAFT", request: requestByConsult[c.id] }) && requestByConsult[c.id]
                    ? ` · ${editBadge({ locked: true, request: requestByConsult[c.id] })}`
                    : ""}
                  {" · "}{(c.unresolved_items || []).length}건 미정{c.created_by_name ? ` · ${c.created_by_name}` : ""}
                </div>
              </button>
              <div className="fc-list-actions">
                <button
                  type="button"
                  className="fc-archive"
                  onClick={() => setDeleteTarget({ type: "consultation", id: c.id, title: c.company_name || "상담", customer: c.company_name })}
                >
                  {isOwner ? "보관" : "삭제 요청"}
                </button>
                {isOwner && (
                  <button
                    type="button"
                    className="fc-hard-del"
                    data-testid={`consult-hard-delete-${c.id}`}
                    onClick={() => setHardDeleteTarget(c)}
                  >
                    삭제
                  </button>
                )}
              </div>
            </div>
          ))}
        </aside>
      </div>

      {deleteTarget && (
        <DeleteRequestModal
          resourceType={deleteTarget.type}
          resourceId={deleteTarget.id}
          resourceTitle={deleteTarget.title}
          customerName={deleteTarget.customer}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); loadList(); refreshAdminNotifications(); }}
        />
      )}
      {batchOpen && (
        <BatchDeleteModal
          resourceType="consultation"
          ids={selectedIds}
          isOwner={isOwner}
          onClose={() => setBatchOpen(false)}
          onDone={(data) => {
            const gone = new Set([...(data?.deleted || []), ...(data?.requested || [])]);
            setSelectedIds((prev) => prev.filter((id) => !gone.has(id)));
            if (currentId && (data?.deleted || []).includes(currentId)) resetLocalSession();
            setBatchOpen(false);
            loadList();
          }}
        />
      )}
      {hardDeleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="consult-hard-delete-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
            <h4 className="font-medium">이 상담을 정말 삭제하시겠습니까?</h4>
            <p className="text-sm text-white/70">상담 제목: {hardDeleteTarget.company_name || hardDeleteTarget.contact_name || "-"}</p>
            <p className="text-sm text-white/70">고객명: {hardDeleteTarget.contact_name || hardDeleteTarget.company_name || "-"}</p>
            <p className="text-sm text-white/70">작성자: {hardDeleteTarget.created_by_name || "-"}</p>
            <p className="text-sm text-white/70">상담 상태: {CONSULT_STATUS_LABEL[hardDeleteTarget.consultation_status] || hardDeleteTarget.consultation_status || "-"}</p>
            <p className="text-sm text-white/70">상담일: {hardDeleteTarget.consulted_at || (hardDeleteTarget.created_at || "").slice(0, 10) || "-"}</p>
            <p className="text-xs text-red-300">삭제한 상담은 복구할 수 없습니다.</p>
            <div className="flex gap-2">
              <button type="button" disabled={hardDeleting} onClick={() => setHardDeleteTarget(null)} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
              <button type="button" disabled={hardDeleting} onClick={runHardDelete} className="bg-red-500 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50" data-testid="consult-hard-delete-submit">
                {hardDeleting ? "삭제 중..." : "삭제 실행"}
              </button>
            </div>
          </div>
        </div>
      )}
      {resetOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="consult-reset-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
            <h4 className="font-medium">새 상담을 시작할까요?</h4>
            <p className="text-sm text-white/70">입력 중인 내용은 사라집니다. 이미 저장된 기존 상담 기록은 삭제되지 않습니다.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setResetOpen(false)} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
              <button
                type="button"
                className="bg-white text-black rounded-lg px-3 py-2 text-sm"
                data-testid="consult-reset-submit"
                onClick={() => { setResetOpen(false); resetLocalSession(); }}
              >
                새 상담 시작
              </button>
            </div>
          </div>
        </div>
      )}
      {requestOpen && currentId && (
        <EditRequestModal
          onClose={() => setRequestOpen(false)}
          onSubmit={async (payload) => {
            try {
              await submitEditRequest(currentId, payload);
              toast.success("수정 승인을 요청했습니다.");
              setRequestOpen(false);
              loadEditRequests();
              openItem({ id: currentId, ...form });
            } catch (e) {
              toast.error(e?.response?.data?.detail || "요청 실패");
            }
          }}
        />
      )}
      {rejectTarget && (
        <RejectEditModal
          onClose={() => setRejectTarget(null)}
          onSubmit={async (note) => {
            try {
              await decideEditRequest(rejectTarget.id, false, note);
              toast.success("요청을 거절했습니다.");
              setRejectTarget(null);
              loadEditRequests();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "거절 실패");
            }
          }}
        />
      )}
      {auditItems && (
        <AuditDrawer items={auditItems} onClose={() => setAuditItems(null)} />
      )}
    </div>
  );
}
